import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { AddressForm, FullAddressComponentsModel } from 'src/providers/address-form/address-form';
import { Addresses } from 'src/providers/customer/addresses';
import { GoogleGeocode } from 'src/providers/google-geocode/google-geocode';
import { MultipleCountryService } from 'src/providers/addresses/multiple-country.service';
import { CurrentAddress } from 'src/providers/addresses/current-address';

import { AutoCompleteAddressModel } from 'src/models/autocomplete-address.model';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { DemoBlockService } from 'src/shared/services/demo-block.service';

import { getAddPropertyError } from './add-property-error';

export interface AddPropertyFormData {
  smallDisplay?: boolean;
  showImportButton?: boolean;
  onPropertyAdded?: (address: any) => Promise<void> | void;
}

@Component({
  selector: 'tidy-add-property-form',
  templateUrl: './add-property-form.component.html',
  styleUrls: ['./add-property-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AddPropertyFormComponent implements OnInit {

  data: AddPropertyFormData;

  addPropertyForm: UntypedFormGroup;
  addressSuggestions: AutoCompleteAddressModel[] = [];
  addPropertyError = '';
  isAddingProperty = false;
  didSelectAddress = false;
  addressConfirmed = false;
  detectedLocationSummary = '';
  isRentalClient = false;
  // Google leaves the postal code out of some places (a street or an area rather than a building),
  // and the API refuses an address without one, so the customer types it in.
  needsZipcode = false;
  submitted = false;
  zipcodeLabel = 'Zip Code';
  zipcodeMask: { mask: string } | null = null;

  readonly goLiveSteps = [
    { step: '01', title: 'Connect your accounts', desc: 'Link your Airbnb, VRBO, or PMS.' },
    { step: '02', title: 'Set your rules', desc: 'Tell TIDY how you want things done.' },
    { step: '03', title: 'Go live', desc: 'TIDY automates your property management.' },
  ];
  activeStepIndex = 0;

  private cdr = inject(ChangeDetectorRef);
  private fb = inject(UntypedFormBuilder);
  private addressFormService = inject(AddressForm);
  private addressesService = inject(Addresses);
  private currentAddress = inject(CurrentAddress);
  private googleGeocode = inject(GoogleGeocode);
  private multipleCountryService = inject(MultipleCountryService);
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private demoBlock = inject(DemoBlockService);

  ngOnInit(): void {
    this.rightSidePanelService.setDialogPageTitle('Add a Property');
    this.isRentalClient = localStorage.getItem('isRentalClient') === 'true';
    this.initForm();
    setTimeout(() => this.focusAddressInput(), 400);
    if (this.isRentalClient) {
      this.animateSteps();
    }
  }

  private animateSteps(): void {
    setTimeout(() => { this.activeStepIndex = 1; this.cdr.markForCheck(); }, 2000);
    setTimeout(() => { this.activeStepIndex = 2; this.cdr.markForCheck(); }, 4000);
  }

  private focusAddressInput(): void {
    const el = document.querySelector('.add-property-form-wrapper tidy-auto-complete input') as HTMLInputElement;
    el?.focus();
  }

  private initForm(): void {
    this.addPropertyForm = this.fb.group({
      countryCode: ['US', Validators.required],
      address: ['', Validators.compose([Validators.maxLength(100), Validators.required])],
      address2: ['', Validators.compose([Validators.maxLength(100)])],
      zipcode: ['', this.multipleCountryService.getCountrySettings('US').validator],
      latitude: [''],
      longitude: [''],
      city: [''],
      state: [''],
      addressNickname: ['']
    });
  }

  onAddressSearch = async () => {
    this.addPropertyError = '';
    this.addressConfirmed = false;
    this.needsZipcode = false;
    this.submitted = false;
    const addressValue = this.addPropertyForm.get('address')?.value;
    if (!addressValue || addressValue.length < 3) {
      this.addressSuggestions = [];
      this.cdr.markForCheck();
      return;
    }
    try {
      this.addressSuggestions = await this.addressFormService.updateSearchWorldwide(addressValue);
    } catch (_err) {
      this.addPropertyError = 'Error searching for address. Please try again.';
      this.addressSuggestions = [];
    }
    this.cdr.markForCheck();
  };

  selectAddressSuggestion = async (suggestion: AutoCompleteAddressModel) => {
    this.addPropertyForm.patchValue({ address: suggestion.display });
    this.addressSuggestions = [];
    this.didSelectAddress = true;
    try {
      const fullAddress: FullAddressComponentsModel = await this.addressFormService.getFullAddressComponents(suggestion.placeId);
      const latLngData: any = await this.googleGeocode.getLatLong(suggestion.placeId);
      this.addPropertyForm.patchValue({
        countryCode: fullAddress.countryCode || 'US',
        zipcode: fullAddress.postalCode,
        city: fullAddress.city || '',
        state: fullAddress.state || '',
        latitude: latLngData?.latitude || '',
        longitude: latLngData?.longitude || ''
      });
      const countryCode = fullAddress.countryCode || 'US';
      const validatorObject = this.multipleCountryService.getCountrySettings(countryCode);
      const zipcodeControl = this.addPropertyForm.get('zipcode');
      zipcodeControl?.setValidators(validatorObject?.validator);
      zipcodeControl?.updateValueAndValidity();
      this.zipcodeLabel = this.multipleCountryService.checkIfContryCodeIsUs(countryCode) ? 'Zip Code' : 'Postal Code';
      this.zipcodeMask = validatorObject?.mask || null;
      this.needsZipcode = !!zipcodeControl?.invalid;
      this.submitted = false;
      this.detectedLocationSummary = this.buildLocationSummary(fullAddress);
      this.addressConfirmed = true;
    } catch (_err) {
      this.addPropertyError = 'Error getting address details. Please try again.';
      this.addressConfirmed = false;
    }
    this.cdr.markForCheck();
  };

  private buildLocationSummary(address: FullAddressComponentsModel): string {
    const parts = [address.city, address.state, address.countryName].filter(Boolean);
    return parts.join(', ');
  }

  submitAddProperty = async () => {
    // Can't add a property to the shared demo account.
    if (this.demoBlock.block()) { return; }
    this.addPropertyError = '';
    if (!this.didSelectAddress) {
      this.addPropertyError = 'Please select an address from the suggestions.';
      this.cdr.markForCheck();
      return;
    }
    if (this.addPropertyForm.get('zipcode')?.invalid) {
      this.showZipcodeField();
      return;
    }
    this.isAddingProperty = true;
    this.cdr.markForCheck();
    try {
      const formValue = this.addPropertyForm.value;
      const addressArray = formValue.address.split(',');
      const addressStateFallback = addressArray[2]?.trim();
      const coords = await this.googleGeocode.getLatLongWithUnit(
        { address1: addressArray[0], address2: formValue.address2, city: formValue.city, state: formValue.state || addressStateFallback, zip: formValue.zipcode },
        { latitude: formValue.latitude, longitude: formValue.longitude }
      );
      const body: any = {
        country_code: formValue.countryCode,
        add_state: formValue.state || addressStateFallback,
        city: formValue.city,
        address1: addressArray[0],
        address2: formValue.address2,
        zip: formValue.zipcode?.trim(),
        latitude: coords.latitude,
        longitude: coords.longitude
      };
      if (formValue.addressNickname) {
        body.address_name = formValue.addressNickname;
      }
      const address = await this.addressesService.createAddress(body);
      this.currentAddress.addressId = address.id;
      localStorage.setItem('region_id', address.region_id);
      if (this.data?.onPropertyAdded) {
        await this.data.onPropertyAdded(address);
      }
      this.rightSidePanelService.closeRightSidePanel();
    } catch (err: any) {
      const { field, message } = getAddPropertyError(err);
      if (field === 'zipcode') {
        this.needsZipcode = true;
        this.submitted = true;
      }
      this.addPropertyError = message;
    } finally {
      this.isAddingProperty = false;
      this.cdr.markForCheck();
    }
  };

  private showZipcodeField(): void {
    this.needsZipcode = true;
    this.submitted = true;
    this.addPropertyError = 'Please enter the zip code for this address.';
    this.cdr.markForCheck();
  }

  goToIntegration = () => {
    this.rightSidePanelService.closeRightSidePanel();
    localStorage.setItem('integrationOptionsBackButton', 'properties');
    this.navCtrl.navigateForward('integration-options');
  };
}
